/**
 * porProvenanceBasis — the TypeScript mirror of migration 698's derivation.
 *
 * P1 of docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md. 678 left
 * `por_record_snapshots.source_cycle_report_id` NULL with the note "there is no
 * reliable derivation ... wiring it is a follow-up, not silently guessed here".
 * 698 wires it for exactly one plan shape and records WHY in
 * `planning_audit_events.payload.source_cycle_report_basis`.
 *
 * This module exists because the basis code is a product surface, not an
 * implementation detail. A NULL provenance column has to read as "unmeasured,
 * and here is why" rather than "broken" — on the latency report, on the
 * canonical commitment view, and in any post-apply verification. That reading
 * requires the same four-way case analysis the migration performs, expressed
 * once, in a pure function, with tests.
 *
 * It is a MIRROR, not the source of truth: the migration derives provenance
 * in-ceremony (§2.3a) and nothing here writes anything. Its job is to let a
 * reader decide the same thing the ceremony decided, and to narrate a NULL
 * honestly. If 698's SQL branches ever change, change these together — the
 * tests below pin the exact string forms the SQL emits, so a drift between the
 * two shows up as a failing assertion rather than as a silently wrong label.
 */

/** The plan-cycle input shape 698 can derive a cycle report from. */
export const DERIVABLE_INPUT_TYPE = 'published_priority_list';

/** Basis codes 698 writes to `planning_audit_events.payload`. */
export type ProvenanceBasis =
  /** Exactly one ratified cycle report on the cycle's priority list — joined. */
  | { code: 'priority_set_join'; matchCount: 1 }
  /** The set exists but carries no ratified report; nothing to point at. */
  | { code: 'no_ratified_report_for_priority_set'; matchCount: 0 }
  /** More than one ratified report. 678's rule: leave NULL where not unambiguous. */
  | { code: 'ambiguous_ratified_reports'; matchCount: number }
  /** Any other input type (or a missing reference) has no cycle report at all. */
  | { code: 'input_type_not_published_priority_list'; matchCount: null };

export type ProvenanceBasisCode = ProvenanceBasis['code'];

/**
 * The exact string 698 stores. Ambiguity is encoded with its count baked into
 * the code (`ambiguous_3_ratified_reports`), which is why parsing needs a
 * regex rather than a set membership test.
 */
export function serializeBasis(basis: ProvenanceBasis): string {
  return basis.code === 'ambiguous_ratified_reports'
    ? `ambiguous_${basis.matchCount}_ratified_reports`
    : basis.code;
}

const AMBIGUOUS_PATTERN = /^ambiguous_(\d+)_ratified_reports$/;

/**
 * Reads a stored basis string back into the structured form.
 *
 * Returns null for anything unrecognised — including 698's initial
 * `not_attempted` sentinel, which is only ever visible if the ceremony aborted
 * before the derivation block, and which must therefore NOT be dressed up as
 * one of the four legitimate outcomes.
 */
export function parseBasis(raw: string | null | undefined): ProvenanceBasis | null {
  if (!raw) return null;

  const ambiguous = AMBIGUOUS_PATTERN.exec(raw);
  if (ambiguous) {
    const matchCount = Number(ambiguous[1]);
    // A count of 0 or 1 is not ambiguity — the SQL cannot emit it, so a string
    // claiming it is corrupt rather than merely unfamiliar.
    if (matchCount < 2) return null;
    return { code: 'ambiguous_ratified_reports', matchCount };
  }

  switch (raw) {
    case 'priority_set_join':
      return { code: 'priority_set_join', matchCount: 1 };
    case 'no_ratified_report_for_priority_set':
      return { code: 'no_ratified_report_for_priority_set', matchCount: 0 };
    case 'input_type_not_published_priority_list':
      return { code: 'input_type_not_published_priority_list', matchCount: null };
    default:
      return null;
  }
}

/** The inputs 698's derivation block reads. */
export interface ProvenanceDerivationInput {
  /** `plan_cycles.input_type`. */
  inputType: string | null | undefined;
  /** `plan_cycles.input_reference_id` — a priority_sets.id for the derivable shape. */
  inputReferenceId: string | null | undefined;
  /** COUNT(*) of ratified `cycle_reports` on that priority list. */
  ratifiedReportCount: number;
}

/**
 * The same four-way case the migration performs, in the same order.
 *
 * Note the guard covers a NULL `input_reference_id` as well as the input type:
 * 698 requires BOTH before it counts anything, because a published-priority-list
 * cycle with no reference has nothing to join against and must not be reported
 * as "no ratified report" (which would imply the set was checked and found bare).
 */
export function deriveProvenanceBasis(input: ProvenanceDerivationInput): ProvenanceBasis {
  if (input.inputType !== DERIVABLE_INPUT_TYPE || !input.inputReferenceId) {
    return { code: 'input_type_not_published_priority_list', matchCount: null };
  }
  if (input.ratifiedReportCount === 1) {
    return { code: 'priority_set_join', matchCount: 1 };
  }
  if (input.ratifiedReportCount <= 0) {
    return { code: 'no_ratified_report_for_priority_set', matchCount: 0 };
  }
  return { code: 'ambiguous_ratified_reports', matchCount: input.ratifiedReportCount };
}

/** True only for the one basis that legitimately yields a non-NULL column. */
export function basisYieldsProvenance(basis: ProvenanceBasis | null): boolean {
  return basis?.code === 'priority_set_join';
}

/**
 * Reader-facing narration of a basis.
 *
 * The whole point of recording a basis is that a downstream surface can say
 * why a number is missing. These strings are written to be readable in a table
 * cell next to a blank, not in a log.
 */
export function narrateBasis(basis: ProvenanceBasis | null): string {
  if (!basis) {
    return 'No derivation basis was recorded — this authorization predates the provenance wiring.';
  }
  switch (basis.code) {
    case 'priority_set_join':
      return 'Joined through the cycle’s priority list: exactly one ratified cycle report.';
    case 'no_ratified_report_for_priority_set':
      return 'The cycle’s priority list has no ratified cycle report, so there is nothing to point at.';
    case 'ambiguous_ratified_reports':
      return `The cycle’s priority list carries ${basis.matchCount} ratified cycle reports. Left unset deliberately — choosing one would be a guess.`;
    case 'input_type_not_published_priority_list':
      return 'This cycle was not planned from a published priority list, so it has no cycle report to trace back to.';
  }
}

/**
 * The provenance state of one authorization, for a surface that must render an
 * absence honestly.
 *
 * `expected` is what separates "unmeasured" from "broken": when the basis says
 * a join was available and the column is still NULL, that is a defect, and a
 * surface that renders it as an ordinary gap hides a regression. That is
 * exactly the failure mode P1's proof-of-life exists to catch, because BOTH
 * provenance columns are unexercised on real data (every prod snapshot predates
 * mig 678).
 */
export interface ProvenanceReading {
  basis: ProvenanceBasis | null;
  /** Whether a non-NULL `source_cycle_report_id` was expected. */
  expected: boolean;
  /** Whether one is actually present. */
  present: boolean;
  /** Present when expected — otherwise a defect worth naming, not a gap. */
  ok: boolean;
  explanation: string;
}

export function readProvenance(args: {
  sourceCycleReportId: string | null | undefined;
  basisRaw: string | null | undefined;
}): ProvenanceReading {
  const basis = parseBasis(args.basisRaw);
  const expected = basisYieldsProvenance(basis);
  const present = Boolean(args.sourceCycleReportId);

  if (expected && !present) {
    return {
      basis,
      expected,
      present,
      ok: false,
      explanation:
        'Defect: the derivation reported a single ratified cycle report, but no cycle report was stamped on the snapshot.',
    };
  }
  if (!expected && present) {
    return {
      basis,
      expected,
      present,
      ok: false,
      explanation:
        'Defect: a cycle report is stamped on the snapshot that the recorded derivation basis does not justify.',
    };
  }
  return { basis, expected, present, ok: true, explanation: narrateBasis(basis) };
}
